import type { TimeWindow, TaskPriority, TaskTypes } from '~/api/types';

export interface GetCurrentTimeProps {
    type: 'start' | 'end';
    isTwoPart?: boolean;
    taskType?: string;
}

export interface AddressInputFields {
    customerName?: string;
    customerId?: string;
    addressStreet?: string;
    addressApt?: string;
    addressCity?: string;
    addressState?: string;
    addressZipCode?: string;
    longitude?: string | number;
    latitude?: string | number;
    currentTaskType?: TaskTypes;
    euid?: string;
}

export interface AddressTabFields {
    date?: Date;
    taskType?: TaskTypes;
    externalTaskType?: string;
    showToDriver?: boolean;
    deliveryLatitude?: string;
    deliveryLongitude?: string;
    deliveryAddressStreet?: string;
    deliveryAddressApt?: string;
    deliveryAddressCity?: string;
    deliveryAddressState?: string;
    deliveryAddressZipCode?: string;
    deliveryCustomerName?: string;
    deliveryCustomerId?: string;
    deliveryEuid?: string;
    pickupLatitude?: string;
    pickupLongitude?: string;
    pickupAddressStreet?: string;
    pickupAddressApt?: string;
    pickupAddressCity?: string;
    pickupAddressState?: string;
    pickupAddressZipCode?: string;
    pickupCustomerName?: string;
    pickupCustomerId?: string;
    pickupEuid?: string;
}

export type AddTaskModalTimeWindow = Partial<TimeWindow<Date>>;

export interface Invoice {
    frontendOnlyId?: string;
    amount?: string;
    referenceNumber?: string;
}

export enum VehicleType {
    SIDE_LOADER = 'Side Loader',
    END_LOADER = 'End Loader'
}

export type TaskCompartment = {
    componentId: string;
    selectedCompartmentId: string;
    capacity: string;
    label: string;
    unit: string;
    isFirst: boolean;
};

export interface TaskRequirement {
    requirement: string;
}

export interface TaskInformationTabFields {
    priority?: TaskPriority;
    deliveryTimeWindows?: Readonly<AddTaskModalTimeWindow>[];
    pickupTimeWindows?: Readonly<AddTaskModalTimeWindow>[];
    invoices?: Readonly<Invoice>[];
    paymentRequired?: boolean;
    deliveryServiceTimeMins?: string;
    pickupServiceTimeMins?: string;
    sizeByCompartment?: Partial<TaskCompartment>[];
    routeId?: string;
    vehicleType?: string | null;
    size?: string;
    weight?: string;
    height?: string;
    equipmentId?: string;
    carKind?: string;
    requirements?: Partial<TaskRequirement>[];
}

export interface InventoryItem {
    name?: string;
    trackingID?: string;
    quantity?: string;
    weight?: string;
}

export interface InventoryItemsTabFields {
    inventoryItems?: Readonly<InventoryItem>[];
}

export interface Note {
    title?: string;
    content?: string;
}

export interface NotesTabFields {
    notes?: Note[];
}

export type ClientTask = AddressTabFields &
    TaskInformationTabFields &
    InventoryItemsTabFields &
    NotesTabFields;

export type VehicleTypeToTranslationKeyMap = {
    [key in VehicleType]: string;
};

export type AddressTabRequiredFieldMap =
    RequiredFieldMapTypeGen<AddressTabFields>;

export type TaskInformationTabRequiredFieldMap =
    RequiredFieldMapTypeGen<TaskInformationTabFields>;

export type InventoryItemsTabRequiredFieldMap =
    RequiredFieldMapTypeGen<InventoryItemsTabFields>;

export type InventoryItemRequiredFieldMap =
    RequiredFieldMapTypeGen<InventoryItem>;

export type NotesTabRequiredFieldMap = RequiredFieldMapTypeGen<NotesTabFields>;

export type ClientTaskRequiredFieldMap = RequiredFieldMapTypeGen<ClientTask>;

export type ClientTaskErrorState = ErrorStateTypeGen<ClientTask>;

export type AddressTabErrorState = ErrorStateTypeGen<AddressTabFields>;

export type TaskInformationTabErrorState =
    ErrorStateTypeGen<TaskInformationTabFields>;

export type InventoryItemsTabErrorState =
    ErrorStateTypeGen<InventoryItemsTabFields>;

export type NotesTabErrorState = ErrorStateTypeGen<NotesTabFields>;

export type TabErrorState =
    | AddressTabErrorState
    | TaskInformationTabErrorState
    | InventoryItemsTabErrorState
    | NotesTabErrorState;

export type FieldToTabErrorStateMap = {
    [key in keyof AddressTabFields]-?: AddressTabErrorState;
} & {
    [key in keyof TaskInformationTabFields]-?: TaskInformationTabErrorState;
} & {
    [key in keyof InventoryItemsTabErrorState]-?: InventoryItemsTabErrorState;
} & {
    [key in keyof NotesTabErrorState]-?: NotesTabErrorState;
};

export enum AddTaskModalTab {
    ADDRESS,
    TASK_INFORMATION,
    INVENTORY_ITEMS,
    NOTES
}
